/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import isNil from 'lodash/isNil';
import React, { useRef } from 'react';
import {
  ScrollbarProps as ReactCustomScrollbarsProps,
  Scrollbars as ReactCustomScrollbars,
} from 'react-custom-scrollbars-2';
import styled from 'styled-components';
import appTheme from '../theme/appTheme.json';
import { ThemeInterface } from '../theme/AppThemeInterface';

const theme: ThemeInterface = appTheme;

const SCROLLBARS_MAIN_CLASS_NAME = 'scrollbars__main';
const SCROLLBARS_THUMB_VERTICAL_CLASS_NAME = 'scrollbars__thumb__vertical';
const SCROLLBARS_THUMB_VERTICAL_SHOWN_CLASS_NAME = 'scrollbars__thumb__vertical__shown';
const SCROLLBARS_THUMB_VERTICAL_HIDDEN_CLASS_NAME = 'scrollbars__thumb__vertical__hidden';

export const SCROLLBARS_THUMB_VERTICAL_STYLE: React.CSSProperties = {
  backgroundColor: theme.scrollbarThumbColor,
  zIndex: 99999,
  borderRadius: '4px',
  display: 'block',
  position: 'relative',
  width: '100%',
};

const StyledScrollbars = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;

  @media print {
    overflow: unset;
  }

  .${SCROLLBARS_THUMB_VERTICAL_SHOWN_CLASS_NAME} {
    opacity: 1;
  }

  .${SCROLLBARS_THUMB_VERTICAL_HIDDEN_CLASS_NAME} {
    opacity: 0;
  }
`;

export interface ScrollbarsProps extends ReactCustomScrollbarsProps {
  verticalThumbAlwaysHidden?: boolean;
}

const Scrollbars = (props: ScrollbarsProps) => {
  const { children, verticalThumbAlwaysHidden, ...rest } = props;
  const scrollbarsInstance = useRef<ReactCustomScrollbars>(null);

  /**
   * Hides all the scrollbars currently visible in the document
   */
  const hideAllScrollbars = () => {
    const shownElements = document.getElementsByClassName(SCROLLBARS_THUMB_VERTICAL_SHOWN_CLASS_NAME);

    for (let i = 0; i < shownElements.length; i++) {
      const elem = shownElements[i];
      elem.classList.remove(SCROLLBARS_THUMB_VERTICAL_SHOWN_CLASS_NAME);
      elem.classList.add(SCROLLBARS_THUMB_VERTICAL_HIDDEN_CLASS_NAME);
    }
  };

  /**
   * Shows the scrollbars of the last child of thumbs
   *
   * @param thumbs HTML elements that have class SCROLLBARS_THUMB_VERTICAL_CLASS_NAME
   */
  const showAreaScrollbars = (thumbs: any) => {
    if (!isNil(thumbs) && thumbs.length) {
      const thumb = thumbs[thumbs.length - 1];

      thumb.classList.remove(SCROLLBARS_THUMB_VERTICAL_HIDDEN_CLASS_NAME);
      thumb.classList.add(SCROLLBARS_THUMB_VERTICAL_SHOWN_CLASS_NAME);
    }
  };

  /**
   * Sets the scrollbars in the appropriate areas when scrolling begins or on mouse enter
   *
   * @param target the target area to show the scrollbars on
   */
  const setScrollbars = (target: any) => {
    hideAllScrollbars();

    if (!isNil(target)) {
      const thumbs = target.getElementsByClassName(SCROLLBARS_THUMB_VERTICAL_CLASS_NAME);
      showAreaScrollbars(thumbs);
    }
  };

  const handleScrollbarMouseEnter = (event: React.MouseEvent<any, MouseEvent>) => {
    setScrollbars(event.currentTarget);
  };

  const handleScrollbarMouseLeave = (event: React.MouseEvent<any, MouseEvent>) => {
    hideAllScrollbars();

    if (!isNil(event.currentTarget)) {
      const closestThumbs = event.currentTarget.closest(`.${SCROLLBARS_MAIN_CLASS_NAME}`);

      if (!isNil(closestThumbs)) {
        const thumbs = closestThumbs.getElementsByClassName(SCROLLBARS_THUMB_VERTICAL_CLASS_NAME);

        if (!isNil(thumbs) && thumbs.length > 1) {
          showAreaScrollbars(thumbs);
        }
      }
    }
  };

  const handleScrollStart = () => {
    if (!isNil(scrollbarsInstance.current)) {
      //@ts-ignore
      setScrollbars(scrollbarsInstance.current.container);
    }
  };

  return (
    <StyledScrollbars>
      <ReactCustomScrollbars
        className={SCROLLBARS_MAIN_CLASS_NAME}
        ref={scrollbarsInstance}
        hideTracksWhenNotNeeded
        renderThumbVertical={({ style, ...props }: any) => {
          return (
            <div
              {...props}
              style={{
                ...style,
                ...SCROLLBARS_THUMB_VERTICAL_STYLE,
                display: verticalThumbAlwaysHidden ? 'none' : SCROLLBARS_THUMB_VERTICAL_STYLE.display,
              }}
              className={`${SCROLLBARS_THUMB_VERTICAL_CLASS_NAME} ${SCROLLBARS_THUMB_VERTICAL_HIDDEN_CLASS_NAME}`}
            />
          );
        }}
        onMouseEnter={handleScrollbarMouseEnter}
        onMouseLeave={handleScrollbarMouseLeave}
        onScrollStart={handleScrollStart}
        {...rest}
      >
        {children}
      </ReactCustomScrollbars>
    </StyledScrollbars>
  );
};

export default Scrollbars;
